// src/modules/units/tyres/shared/tyre-format.js
//
// Display formatting for the tyre subsystem. One home for these so every tyre
// view (register, detail, action screens) renders the same value identically —
// no drift in how a distance, a tread depth or a fitment reads between screens.
// Everything here returns a display-ready STRING; callers that inject into
// innerHTML must still run text through esc().

export function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const isBlank = (v) => v === null || v === undefined || v === '';

// ---------------------------------------------------------------------------
// Number grouping — Rand and kilometres both read "1 234 567", never "1,234,567".
//
// The two separators are named here so the convention is set in ONE place. We
// deliberately don't use the browser's default locale (that's what produced the
// commas) and don't hard-code a locale string either: en-ZA disagrees with itself
// across engines on the decimal mark, so the same build would render differently
// machine to machine. Applying the grouping ourselves means every user sees the
// same thing.
//
// THOUSANDS is a non-breaking space, so a number never wraps across two lines.
// DECIMAL is a comma (the SA convention that goes with space grouping) — change
// this single character to '.' if you'd rather read "1 234 567.89".
// ---------------------------------------------------------------------------
const THOUSANDS = '\u00a0';  // non-breaking space
const DECIMAL = '.';

// `decimals` = null → whole number (distances, counts). A number → fixed decimals.
function groupNumber(value, decimals = null) {
  const neg = value < 0;
  const abs = Math.abs(value);
  const fixed = decimals === null ? String(Math.round(abs)) : abs.toFixed(decimals);
  const [intPart, fracPart] = fixed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, THOUSANDS);
  const body = fracPart ? `${grouped}${DECIMAL}${fracPart}` : grouped;
  return neg ? `-${body}` : body;
}

export function formatKm(v) {
  if (isBlank(v)) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `${groupNumber(n)} km` : '—';
}

export function formatTread(v) {
  if (isBlank(v)) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';

  const rounded = Math.round((n + Number.EPSILON) * 100) / 100;
  const display = rounded.toFixed(2).replace(/\.?0+$/, '');
  return `${display} mm`;
}

export function formatShortDate(v) {
  if (isBlank(v)) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

export function formatDateTime(v) {
  if (isBlank(v)) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function formatMoney(v) {
  if (isBlank(v)) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? groupNumber(n, 2) : '—';
}

// Whole-number counts (tyre totals, result counts) — same space grouping, no unit.
export function formatCount(v) {
  if (isBlank(v)) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? groupNumber(n) : '—';
}

// A currentWheelId the backend uses as a sentinel, not a real position: the
// sensor still reports through the unit's IMEI/gateway, but the tyre has been
// taken OFF the wheel. It is deliberately un-decodable as a genuine position —
// its axle digits are "18", and a unit has at most 16 axles (17 = spare) — so it
// can never collide with a wheel that exists. A tyre on this "position" is paired
// for DATA, not IN SERVICE, which is why status derivation (tyre-status.js) treats
// it as Fitted rather than In Service. Defined here because it is a wheel-id
// encoding fact; tyre-status.js imports the predicate rather than re-hardcoding
// the number, so the sentinel lives in exactly one place.
export const UNPAIRED_WHEEL_ID = '1180';

export function isUnpairedWheelId(wheelId) {
  return !isBlank(wheelId) && String(wheelId).trim() === UNPAIRED_WHEEL_ID;
}

// Wheel position label, matching the per-wheel form's convention:
// spare axle (17) → "S-<pos>", otherwise "A<axle>-T<pos>". The unpaired sentinel
// reads "Unpaired" rather than decoding to a fictitious "A18-T1".
export function formatWheelPosition(wheelId) {
  if (isBlank(wheelId)) return '';
  if (isUnpairedWheelId(wheelId)) return 'Unpaired';
  const w = String(wheelId);
  const pos = w.slice(0, 1);
  const axle = parseInt(w.slice(1, 3), 10);
  if (axle === 17) return `S-${pos}`;
  if (Number.isNaN(axle)) return w;
  return `A${axle}-T${pos}`;
}

// ---------------------------------------------------------------------------
// Fitment — three facts, each addressable on its own.
//
// A fitment is a sensor, a unit and a wheel position. Joining them into one
// "0056A · A2-T4 · 537614590" string made the reader learn the order, and it
// actively misled whenever a part was missing: with unitName absent the label
// collapsed to "A2-T4 · 537614590", which reads as though the position were the
// vehicle. Labelled columns can't do that — an empty Unit column says "we don't
// know", which is the truth.
//
// `blank` is what to show when a fact isn't there. Screens take the default em
// dash; the CSV passes '' so an empty cell stays empty.
// ---------------------------------------------------------------------------
export function fitmentSensorId(tyre, blank = '—') {
  return isBlank(tyre?.currentSensorId) ? blank : String(tyre.currentSensorId);
}

// unitName is not in GET /api/tyre yet — only unitId/unitType are — so this falls
// back to the raw id, which can at least be cross-referenced, rather than showing
// nothing at all. Drop the second line once the backend request lands.
export function fitmentUnitName(tyre, blank = '—') {
  if (!isBlank(tyre?.unitName)) return String(tyre.unitName);
  if (!isBlank(tyre?.unitId)) return String(tyre.unitId);
  return blank;
}

export function fitmentPosition(tyre, blank = '—') {
  return formatWheelPosition(tyre?.currentWheelId) || blank;
}

// The joined form, kept for the one place a fitment is a line of prose rather
// than a column: the association form's conflict panel, where "Currently fitted"
// has to sit on one line. Derived from the three above so there is still exactly
// one source for each fact. Missing parts are dropped, not dashed.
export function fitmentLabel(tyre) {
  const parts = [
    fitmentUnitName(tyre, ''),
    fitmentPosition(tyre, ''),
    fitmentSensorId(tyre, '')
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

// Sensor metric formatters (units confirmed): pressure in bar (2dp), voltage in
// V (2dp), temperature in whole °C. Available for the reports and the detail
// telemetry pane.
export function formatPressure(v) {
  if (isBlank(v)) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? groupNumber(n, 2) : '—';  // number only, 2dp; unit added by caller
}
export function formatVoltage(v) {
  if (isBlank(v)) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `${n.toFixed(2)} V` : '—';
}
export function formatTemperature(v) {
  if (isBlank(v)) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `${Math.round(n)}°C` : '—';
}

// Rotation is shown as a STATE, not a raw count. A large magnitude means the
// wheel is turning ("Rolling"); a small one means it's stopped; the sentinel
// -107 means the sensor failed to report. Returns display text + a tone token
// ('tone' to match tm-tone-* / actionTone / tyreStatusTone elsewhere). Missing
// or non-numeric data yields a neutral dash rather than falsely reading as
// "Rolling" (parseInt(undefined) is NaN, and NaN < 1000 is false — the trap in
// the original snippet).
const ROTATION_FAILED_CODE = -107;
const ROTATION_STOPPED_MAX = 1000;  // |value| below this = not rolling

export function rotationStatus(v) {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return { text: '—', tone: null };
  if (Math.abs(n) < ROTATION_STOPPED_MAX) {
    return n === ROTATION_FAILED_CODE
      ? { text: 'Failed', tone: 'red' }
      : { text: 'Stopped', tone: 'slate' };
  }
  return { text: 'Rolling', tone: 'accent-green' };
}